import { useEffect, useState } from "react";

export interface TimePickerProps {
  feedbackValid?: string;
  feedbackInvalid?: string;
  validator?: (time: string | null) => {
    valid?: boolean;
    feedback?: string;
    noValidate?: boolean;
  } | void;
  validated?: boolean;
  onFocus?: () => void;
}

const useTimePicker = (
  defaultValue: string | null | undefined,
  p?: TimePickerProps,
) => {
  const [time, setTime] = useState<string | null | undefined>(defaultValue);
  const [valid, setValid] = useState<boolean | undefined>(undefined);
  const [invalid, setInvalid] = useState(false);
  const [feedbackValid, setFeedbackValid] = useState(p?.feedbackValid ?? "");
  const [feedbackInvalid, setFeedbackInvalid] = useState(
    p?.feedbackInvalid ?? "Please select a valid clock in time!",
  );
  const validated = p?.validated ?? false;

  useEffect(() => {
    if (p?.validator) {
      if (time === undefined) {
        setValid(undefined);
        setInvalid(true);
        return;
      }
      const res = p.validator(time);
      if (res) {
        if (res.noValidate !== undefined) {
          if (res.noValidate) {
            setValid(false);
            setInvalid(false);
            return;
          }
        }
        if (res?.valid) {
          setValid(true);
          setInvalid(false);
          if (res.feedback) {
            setFeedbackValid(res.feedback);
          }
        } else {
          setValid(false);
          setInvalid(true);
          if (res?.feedback) {
            setFeedbackInvalid(res.feedback);
          }
        }
      }
    }
  }, [p, time, validated]);

  const peek = () => {
    return {
      time,
      valid: validated ? (time !== null ? valid : false) : undefined,
      invalid: validated && time !== null ? invalid : false,
      feedbackValid,
      feedbackInvalid,
    };
  };

  const onFocus = () => {
    if (p?.onFocus) {
      p.onFocus();
    }
  };

  return [
    {
      time,
      valid: validated ? (time !== null ? valid : false) : undefined,
      invalid: validated && time !== null ? invalid : false,
      feedbackValid,
      feedbackInvalid,
    },
    {
      setTime,
      peek,
      onFocus,
      setValid,
      setInvalid,
      setFeedbackValid,
      setFeedbackInvalid,
    },
  ] as const;
};

export default useTimePicker;
